<template>
  <div class="mortgage-calculator-container">
    <div
      id="mortgage-calculator"
      class="bg-line-lighter/70 p-4 rounded flex flex-col items-start mb-4"
    >
      <slot name="heading">
        <h2 class="uppercase text-lg md:text-xl mb-2">
          {{ $t('calculator.title') }}
        </h2>
      </slot>

      <!-- Display Results -->
      <div class="results grid grid-cols-2 gap-x-4 mb-2">
        <div class="text-lg">{{ $t('calculator.monthlyPayments') }}</div>
        <div class="text-lg font-bold">
          {{ formatCurrency(Math.round(+(Math.round(monthlyPayment) || 0))) }}
        </div>
        <div>{{ $t('calculator.principalInterest') }}</div>
        <div>
          {{ formatCurrency(Math.round(+(principalAndInterest || 0))) }}
        </div>
        <div>{{ $t('calculator.propertyTax') }}</div>
        <div>
          {{ formatCurrency(Math.round(+(monthlyPropertyTax || 0))) }}
        </div>
        <div>{{ $t('calculator.associationFee') }}</div>
        <div>
          {{ formatCurrency(Math.round(+(monthlyAssociationFee || 0))) }}
        </div>
        <template v-if="!hideInsuranceField">
          <div>{{ $t('calculator.insuranceFee') }}</div>
          <div>
            {{ formatCurrency(Math.round(+(monthlyInsuranceFee || 0))) }}
          </div>
        </template>
      </div>

      <div class="bg-line-light h-px w-full mt-2 mb-5"></div>

      <div
        class="grid md:grid-flow-col grid-cols-1 grid-rows-[auto,auto] gap-x-3 md:mb-4 w-full"
        :class="[
          hideInsuranceField
            ? 'md:grid-cols-[1fr,1.4fr,2fr]'
            : 'md:grid-cols-[1fr,1fr,1fr,1fr]'
        ]"
      >
        <!-- Label and Input for Home Price -->
        <div>{{ $t('calculator.homePrice') }}</div>
        <AppInput
          id="homePrice"
          v-model.number="homePrice"
          name="homePrice"
          class="w-full variant-outline-lg mb-2"
          type="money"
        />

        <!-- Label and Input for Property Tax Yearly -->
        <div>{{ $t('calculator.propertyTaxYearly') }}</div>
        <AppInput
          id="propertyTax"
          v-model.number="yearlyPropertyTax"
          name="propertyTax"
          class="w-full variant-outline-lg mb-2"
          type="money"
        />

        <!-- Label and Input for Association Fee -->
        <div>
          {{ $t('calculator.associationFeeMonthly') }}
        </div>
        <AppInput
          id="monthlyAssociationFee"
          v-model.number="monthlyAssociationFee"
          name="monthlyAssociationFee"
          class="w-full variant-outline-lg mb-2"
          type="money"
        />

        <!-- Label and Input for Insurance Fee -->
        <template v-if="!hideInsuranceField">
          <div>
            {{ $t('calculator.insuranceFeeMonthly') }}
          </div>
          <AppInput
            id="monthlyInsuranceFee"
            v-model.number="monthlyInsuranceFee"
            name="monthlyInsuranceFee"
            class="w-full variant-outline-lg mb-2"
            type="money"
          />
        </template>
      </div>

      <div
        class="grid md:grid-flow-col grid-cols-1 md:grid-cols-3 grid-rows-[auto,auto] gap-x-3 w-full"
      >
        <!-- Label and Input for Down Payment -->
        <label for="downPayment">{{ $t('calculator.downPayment') }}</label>
        <div class="group-inputs">
          <AppInput
            id="downPaymentPercentage"
            :model-value="downPaymentPercentage"
            name="downPaymentPercentage"
            class="w-full variant-outline-lg mb-2 max-w-[4.5rem] narrow-padding"
            type="percent"
            @update:model-value="setDownPaymentPercentage"
          />
          <AppInput
            id="downPayment"
            :model-value="downPayment"
            name="downPayment"
            class="w-full variant-outline-lg mb-2"
            type="money"
            @update:model-value="setDownPayment"
          />
        </div>

        <!-- Label and Input for Interest Rate -->
        <label for="interestRate">{{ $t('calculator.interestRate') }}</label>
        <AppInput
          id="interestRate"
          v-model.number="interestRate"
          name="interestRate"
          class="w-full variant-outline-lg mb-2"
          type="percent"
          step="0.5"
          :placeholder="$t('calculator.interestRatePlaceholder')"
        />

        <!-- Label and Dropdown for Loan Type -->
        <label for="loanType">{{ $t('calculator.loanType') }}</label>
        <AppSelector
          id="loanType"
          v-model="loanType"
          name="loanType"
          class="w-full variant-outline-lg mb-2"
          :options="[
            { value: 30, text: $t('calculator.loanType30Year') },
            { value: 20, text: $t('calculator.loanType20Year') },
            { value: 15, text: $t('calculator.loanType15Year') },
            { value: 10, text: $t('calculator.loanType10Year') }
          ]"
        >
        </AppSelector>
      </div>
    </div>
    <ClientOnly>
      <AnalyticsPaymentChart
        v-if="showChart"
        class="mt-4 md:mt-8"
        :data="[
          {
            text: $t('calculator.principalInterestLegend'),
            value: principalAndInterest,
            color: '#6A7FDB'
          },
          {
            text: $t('calculator.propertyTaxLegend'),
            value: monthlyPropertyTax,
            color: '#FFD166'
          },
          {
            text: $t('calculator.associationFeeLegend'),
            value: monthlyAssociationFee,
            color: '#DA4167'
          }
        ]"
        :total="monthlyPayment"
      />
      <!-- #49BEAA -->
      <!-- #53DD6C -->
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
const AnalyticsPaymentChart = defineAsyncComponent(
  () => import('@/async-components/Analytics/PaymentChart.vue')
);
const AppInput = defineAsyncComponent(
  () => import('@/async-components/App/Input.vue')
);
const AppSelector = defineAsyncComponent(
  () => import('@/async-components/App/Selector.vue')
);

const { language } = useLanguage();

const props = defineProps({
  listPrice: {
    type: Number,
    default: 0
  },
  propertyTax: {
    type: Number,
    default: 0
  },
  yearlyAssociationFee: {
    type: Number,
    default: 0
  },
  showChart: {
    type: Boolean
  },
  hideInsuranceField: {
    type: Boolean
  }
});

const homePrice = ref<number>(props.listPrice || 0);
const yearlyPropertyTax = computed(() => homePrice.value * 0.02); // 2%
const monthlyAssociationFee = ref(
  +(+(props.yearlyAssociationFee || 0)).toFixed(4)
);
const downPaymentPercentage = ref(30);
const downPayment = ref(homePrice.value * 0.4);
const interestRate = ref(7.5); // 7.5%
const loanType = ref(30);

function formatCurrency(v: number) {
  return '$ ' + Intl.NumberFormat(language.value).format(v);
}

const yearlyInsuranceFeeDefault = computed<number>(
  () => Math.round(homePrice.value * 0.007) // 0.7%
);
const monthlyInsuranceFeeDefault = computed<number>(() =>
  Math.round(yearlyInsuranceFeeDefault.value / 12)
);
const monthlyInsuranceFee = ref<number>(monthlyInsuranceFeeDefault.value);
// const yearlyInsuranceFee = computed(() => monthlyInsuranceFee.value * 12);

const principalAndInterest = computed<number>(() => {
  const principal =
    homePrice.value - (homePrice.value * downPaymentPercentage.value) / 100;
  const monthlyInterestRate = interestRate.value / 100 / 12;
  const numberOfPayments = loanType.value * 12; // Loan type represents the number of years

  // Monthly mortgage payment
  const mortgagePayment =
    principal *
    (monthlyInterestRate /
      (1 - Math.pow(1 + monthlyInterestRate, -numberOfPayments)));

  return mortgagePayment;
});

const monthlyPropertyTax = computed(() => {
  // Property tax is annual, so we divide by 12 to get the monthly amount
  return yearlyPropertyTax.value / 12;
});

const monthlyPayment = computed(() => {
  return (
    principalAndInterest.value +
    monthlyPropertyTax.value +
    monthlyAssociationFee.value +
    (props.hideInsuranceField ? 0 : monthlyInsuranceFee.value)
  );
});

function setDownPayment(value: string | number) {
  const numericValue = +value;

  if (numericValue === downPayment.value) {
    return;
  }

  if (downPayment.value !== numericValue) {
    downPayment.value = numericValue;
  }

  const newValue = Math.round((numericValue * 100) / homePrice.value);

  if (Math.round(downPaymentPercentage.value * 10000) / 10000 !== newValue) {
    downPaymentPercentage.value = newValue;
  }
}

function setDownPaymentPercentage(percentage: string | number | null = null) {
  if (percentage === null) {
    percentage = downPaymentPercentage.value;
  }

  const numericPercentage = +percentage;
  const newValue = Math.round(numericPercentage);
  const newPaymentValue = (homePrice.value * numericPercentage) / 100;

  if (
    newValue === downPaymentPercentage.value &&
    newPaymentValue === downPayment.value
  ) {
    return;
  }

  downPaymentPercentage.value = newValue;

  if (newPaymentValue !== downPayment.value) {
    downPayment.value = newPaymentValue;
  }
}

watch(homePrice, () => setDownPaymentPercentage(null), { immediate: true });
</script>

<style>
/* Add your CSS styling here */
</style>
